import React, { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { useForm } from "../FormContext";
import { ChevronDown, Check, X } from "lucide-react";

interface SelectOption {
  label: string;
  value: string | number;
  disabled?: boolean;
}

interface SelectFieldProps {
  name: string;
  value?: string | string[];
  onChange?: (value: string | string[]) => void;
  onBlur?: () => void;
  placeholder?: string;
  required?: boolean;
  className?: string;
  options?: SelectOption[];
  mode?: "multiple" | "tags";
  disabled?: boolean;
  insideModal?: boolean;
}

export const SelectFieldComponent: React.FC<SelectFieldProps> = ({
  name,
  value,
  onChange,
  onBlur,
  placeholder,
  required,
  className = "",
  options = [],
  mode,
  disabled,
  insideModal = false,
}) => {
  const { setFieldValue, setFieldTouched, validateField } = useForm();

  const handleChange = (newValue: string | string[]) => {
    setFieldValue(name, newValue);
    if (onChange) {
      onChange(newValue);
    }
  };

  const handleBlur = () => {
    setFieldTouched(name, true);
    validateField(name);
    if (onBlur) {
      onBlur();
    }
  };

  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const wrapperRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const dropdownContentRef = useRef<HTMLDivElement>(null);
  const { fields } = useForm();
  const fieldValue =
    fields[name]?.value || value || (mode === "multiple" ? [] : "");
  const fieldErrors = fields[name]?.errors || [];
  const hasError = fieldErrors.length > 0;



  // Memoize the close dropdown handler
  const closeDropdown = useCallback(() => {
    setIsOpen(false);
    setSearchTerm("");
    handleBlur();
  }, [handleBlur]);

  // Handle clicks outside using a capture phase listener
  useEffect(() => {
    if (!isOpen) return;

    function handleGlobalClick(e: MouseEvent) {
      // Get references to current DOM elements
      const selectEl = wrapperRef.current;
      const dropdownEl = dropdownRef.current;
      const targetEl = e.target as Node;
      
      // Check if click is outside both elements
      const isOutsideSelect = selectEl && !selectEl.contains(targetEl);
      const isOutsideDropdown = dropdownEl && !dropdownEl.contains(targetEl);
      
      if (isOutsideSelect && isOutsideDropdown) {
        // Close the dropdown asynchronously to avoid React state update conflicts
        setTimeout(() => closeDropdown(), 0);
      }
    }

    // Use capture phase to get event before it reaches other handlers
    document.addEventListener('click', handleGlobalClick, true);
    
    return () => {
      document.removeEventListener('click', handleGlobalClick, true);
    };
  }, [isOpen, closeDropdown]);
  
  // Close on escape key
  useEffect(() => {
    if (!isOpen) return;
    
    function handleEscapeKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        closeDropdown();
      }
    }
    
    document.addEventListener('keydown', handleEscapeKey);
    return () => document.removeEventListener('keydown', handleEscapeKey);
  }, [isOpen, closeDropdown]);

  // Calculate dropdown position with enhanced modal support
  const calculateDropdownPosition = useCallback(() => {
    if (!buttonRef.current) return;

    const buttonRect = buttonRef.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    
    // Estimate dropdown height based on number of options (capped at 240px)
    const filteredOpts = options.filter(option => 
      option.label.toLowerCase().includes(searchTerm.toLowerCase())
    );
    const dropdownHeight = Math.min(240, filteredOpts.length * 36 + 60);
    
    // Check if there's enough space below
    const spaceBelow = viewportHeight - buttonRect.bottom;
    const spaceAbove = buttonRect.top;
    
    let top = buttonRect.bottom + 4; // Small gap
    let left = buttonRect.left;
    const width = buttonRect.width;

    // Positioning logic with improved edge case handling
    // Only position above if there's really not enough space below AND more space above
    if (spaceBelow < dropdownHeight && spaceAbove > dropdownHeight && spaceAbove > spaceBelow) {
      // Position above
      top = buttonRect.top - dropdownHeight;
    } else {
      // Default - position below with available space
      top = buttonRect.bottom + 4; // Small gap
      
      // If we're in a modal and at the edge, ensure it's visible even if it means overflow
      if (insideModal && spaceBelow < dropdownHeight) {
        // Ensure at least 100px of the dropdown is visible if possible
        const minVisibleHeight = Math.min(100, dropdownHeight);
        if (spaceBelow < minVisibleHeight) {
          // If we can't show minimum height below, try above
          if (spaceAbove > minVisibleHeight) {
            top = buttonRect.top - dropdownHeight;
          }
        }
      }
    }

    // If inside a modal, ensure the dropdown stays within viewport boundaries
    if (insideModal) {
      // Ensure dropdown doesn't go off screen to the right
      if (left + width > viewportWidth - 10) {
        left = viewportWidth - width - 10;
      }
      
      // Ensure dropdown doesn't go off screen to the left
      if (left < 10) {
        left = 10;
      }
      
      // Make sure dropdown is visible at the bottom
      if (top + dropdownHeight > viewportHeight - 10) {
        top = viewportHeight - dropdownHeight - 10;
      }
      
      // Make sure dropdown is visible at the top
      if (top < 10) {
        top = 10;
      }
    }

    setDropdownPosition({
      top,
      left,
      width,
    });
  }, [options, searchTerm, insideModal]);

  // Recalculate position when dropdown opens or content changes
  useEffect(() => {
    if (isOpen) {
      calculateDropdownPosition();
      
      const handleResize = () => calculateDropdownPosition();
      
      // Use requestAnimationFrame to ensure smooth updates during scrolling
      const handleScrollRAF = () => {
        requestAnimationFrame(calculateDropdownPosition);
      };
      
      window.addEventListener('resize', handleResize);
      window.addEventListener('scroll', handleScrollRAF, true);
      
      return () => {
        window.removeEventListener('resize', handleResize);
        window.removeEventListener('scroll', handleScrollRAF, true);
      };
    }
  }, [isOpen, calculateDropdownPosition]);

  // Filter options based on search term
  const filteredOptions = options.filter((option) =>
    option.label.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  // Get display value for single select
  const getDisplayValue = () => {
    if (mode === "multiple") return "";
    const selectedOption = options.find((opt) => opt.value === fieldValue);
    return selectedOption ? selectedOption.label : "";
  };

  // Handle selection for single and multiple modes
  const handleSelect = (optValue: string | number) => {
    if (mode === "multiple") {
      const currentValues = Array.isArray(fieldValue) ? fieldValue : [];
      const newValues = currentValues.includes(optValue.toString())
        ? currentValues.filter((v) => v !== optValue.toString())
        : [...currentValues, optValue.toString()];
      handleChange(newValues);
    } else {
      handleChange(optValue.toString());
      setIsOpen(false);
    }
  };

  // Check if option is selected in multiple mode
  const isSelected = (optValue: string | number) => {
    if (mode === "multiple") {
      return (
        Array.isArray(fieldValue) && fieldValue.includes(optValue.toString())
      );
    }
    return fieldValue === optValue.toString();
  };

  // Remove a tag in multiple mode
  const removeTag = (value: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const newValues = Array.isArray(fieldValue)
      ? fieldValue.filter((v) => v !== value)
      : [];
    handleChange(newValues);
  };

  return (
    <div className="relative w-full" ref={wrapperRef}>
      {/* Select Control */}
      <div
        ref={buttonRef}
        onClick={() => {
          if (!disabled) {
            // Calculate position before opening to avoid flicker
            if (!isOpen) {
              calculateDropdownPosition();
            }
            setIsOpen(!isOpen);
          }
        }}
        className={`flex items-center justify-between w-full px-3 py-2 text-sm border rounded-md outline-none cursor-pointer ${disabled ? "bg-gray-100 dark:bg-darkCardBackground cursor-not-allowed" : ""}  
          ${isOpen ? "border-blue-500 ring-2 ring-blue-200 dark:ring-blue-800" : "border-gray-300 dark:border-gray-700"} 
          ${hasError ? "border-red-500" : ""}
          focus:outline-none dark:text-white transition-all duration-150 ${className}`}
      >
        {/* For multiple selection mode, show tags */}
        {mode === "multiple" &&
        Array.isArray(fieldValue) &&
        fieldValue.length > 0 ? (
          <div className="flex flex-wrap gap-1">
            {fieldValue.map((val) => {
              const opt = options.find((o) => o.value.toString() === val);
              return (
                <span
                  key={val}
                  className="bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-100 text-xs rounded px-1.5 py-0.5 flex items-center"
                >
                  {opt?.label || val}
                  <X
                    size={12}
                    className="ml-1 cursor-pointer"
                    onClick={(e) => removeTag(val, e)}
                  />
                </span>
              );
            })}
            {fieldValue.length > 0 && (
              <input
                type="text"
                className="flex-1 min-w-[60px] bg-transparent border-none focus:outline-none dark:text-white text-sm"
                placeholder=""
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onClick={(e) => e.stopPropagation()}
              />
            )}
          </div>
        ) : (
          // For single selection or empty multiple
          <span
            className={`${!fieldValue && placeholder ? "text-gray-400 dark:text-gray-500" : ""}`}
          >
            {mode === "multiple" ? (
              <input
                type="text"
                className="w-full bg-transparent border-none focus:outline-none dark:text-white text-sm"
                placeholder={placeholder}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onClick={(e) => e.stopPropagation()}
              />
            ) : (
              getDisplayValue() || placeholder
            )}
          </span>
        )}
        <ChevronDown
          size={16}
          className={`transition-transform duration-200 ${isOpen ? "transform rotate-180" : ""}`}
        />
      </div>

      {/* Dropdown rendered with Portal to avoid modal context issues */}
      {isOpen && createPortal(
        <div
          ref={dropdownRef}
          className="fixed z-[99999] bg-white dark:bg-darkCardBackground border border-gray-200 dark:border-gray-700 rounded-md shadow-lg"
          style={{
            top: `${dropdownPosition.top}px`,
            left: `${dropdownPosition.left}px`,
            width: `${dropdownPosition.width}px`,
            maxHeight: "240px",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div
            ref={dropdownContentRef}
            className="overflow-y-auto max-h-[240px]"
          >
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option) => (
                <div
                  key={option.value}
                  className={`px-2 py-1.5 text-sm cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 
                    ${isSelected(option.value) ? "bg-blue-50 dark:bg-blue-900/30" : ""}
                    ${option.disabled ? "opacity-50 cursor-not-allowed" : ""}`}
                  onClick={() => !option.disabled && handleSelect(option.value)}
                >
                  <div className="flex items-center justify-between">
                    <span className="dark:text-white">{option.label}</span>
                    {isSelected(option.value) && (
                      <Check size={16} className="text-blue-500" />
                    )}
                  </div>
                </div>
              ))
            ) : (
              <div className="px-3 py-2 text-gray-500 dark:text-gray-400">
                No options found
              </div>
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};
